import { MouseEvent } from 'react';
import { ChevronsUp, Info, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Image, PriceBadge, Button, Paragraph } from '@app/shared/ui/atoms/Common';
import { PulsingGlowWrapper } from '@app/shared/ui/molecules/Home/PulsingGlowWrapper';
import { SettlementProgress } from '@app/shared/ui/molecules/Home';
import { Settlement as SettlementType } from '@app/store/slices/settlements/settlementsTypes';
import { PriceType } from '@app/shared/types/common';
import { giftImage } from '@app/assets';
import { useSettlementClickAction } from '@app/shared/hooks/useSettlementClickAction';
import { SettlementModalAction } from '@app/shared/types/pages/home';
import { SETTLEMENT_FILL_BAR } from '@app/shared/constants/settlements';
import { Popover, PopoverContent, PopoverTrigger } from '@app/shared/ui/molecules/Common';
import { cn, formatTonIncome, telegramVibrate } from '@app/shared/utils/common';
import { Tutorial } from '@app/shared/ui/organisms/Common';
import { useAppSelector } from '@app/store/hooks';

const CONNECTOR_CLIP_PATH =
  '[clip-path:polygon(0_0,100%_0,100%_100%,calc(100%-70px)_100%,calc(100%-70px)_10px,0_5px)]';

const ALIGN_CLASSES = {
  left: {
    cardContainer: 'items-start',
    flexDirection: 'flex-row',
    boostArrowContainer: 'rounded-r-[11px]',
    connectorSide: 'right-0',
    connectorClipPath: CONNECTOR_CLIP_PATH,
    connectorArrowFlip: '',
    plusIcon: 'right-1',
  },
  right: {
    cardContainer: 'items-end',
    flexDirection: 'flex-row-reverse',
    boostArrowContainer: 'rounded-l-[11px]',
    connectorSide: 'left-0',
    connectorClipPath: `${CONNECTOR_CLIP_PATH} -scale-x-100`,
    connectorArrowFlip: '-scale-x-100',
    plusIcon: 'left-1',
  },
};

type Alignment = 'left' | 'right';

interface SettlementImageProps {
  imageUrl: string;
  name: string;
  className?: string;
}

interface SettlementProps extends SettlementImageProps {
  price?: number;
  imageClassName?: string;
  onClick?: () => void;
  isLocked?: boolean;
}

interface SettlementCardProps {
  settlement: SettlementType;
  align: Alignment;
  shouldPulse: boolean;
  isFirstItem: boolean;
  isSecondItem: boolean;
  isThirdItem: boolean;
  isFourthItem: boolean;
  onClick: (settlement: SettlementType, action: SettlementModalAction) => void;
  onGiftClick: (nodeLevel: number) => void;
}

interface SettlementFeaturesProps {
  settlement: SettlementType;
  isFirstItem?: boolean;
}

interface SettlementConnectorProps {
  align: Alignment;
  isNextSettlementPurchased: boolean;
  isPurchased: boolean;
}

interface SettlementInfoCardProps
  extends SettlementCardProps, Omit<SettlementConnectorProps, 'isPurchased'> {
  withConnector: boolean;
  id?: string;
}

export const SettlementBackground = () => (
  <div className="relative size-full">
    <div
      className={cn(
        'absolute -right-1 -top-1 size-full rounded-[11px]',
        'shadow-home-orange',
        'bg-home-gradient-orange'
      )}
    />
    <div
      className={cn('absolute inset-0 rounded-[11px] backdrop-blur-2xl', 'bg-home-gradient-green')}
    />
  </div>
);

export const SettlementImage = ({ imageUrl, name, className }: SettlementImageProps) => (
  <Image
    src={imageUrl}
    className={cn(
      'absolute right-0 top-0 aspect-square size-[110%] max-w-[unset] object-contain',
      className
    )}
    alt={`${name} settlement`}
  />
);

export const Settlement = ({
  imageUrl,
  name,
  price,
  className,
  imageClassName,
  isLocked,
}: SettlementProps) => (
  <div className={cn('relative size-[105px]', className)}>
    <SettlementBackground />
    <SettlementImage imageUrl={imageUrl} name={name} className={imageClassName} />
    {isLocked ? (
      <Lock className="absolute top-0 right-0" />
    ) : (
      price && (
        <PriceBadge
          type={PriceType.TON}
          price={price}
          size="large"
          className="right-0 top-0 px-1.5 py-0.5 text-xs"
        />
      )
    )}
  </div>
);

export const SettlementFeatures = ({ settlement, isFirstItem }: SettlementFeaturesProps) => {
  const { t } = useTranslation();
  const settlementAmount = SETTLEMENT_FILL_BAR.find((item) => item > settlement.purchasedCount);

  return (
    <>
      <div
        className={cn(
          'flex flex-col gap-1 px-[10px] pt-1 pb-1 text-2xs leading-[1.8]',
          'text-blue-muted'
        )}
      >
        <div
          className={cn(
            'flex flex-col flex-1  min-h-[93px] max-h-[93px] gap-3',
            settlement.isPurchased && 'justify-between gap-1'
          )}
        >
          <div className="space-y-[2px] relative">
            <div className="flex flex-col items-center gap-1 mt-1">
              {/*<Paragraph>{t('settlements.filling')}</Paragraph>*/}
              <Popover>
                <PopoverTrigger asChild>
                  <div
                    className={cn(
                      'flex items-center gap-1 w-full max-h-[10px]',
                      isFirstItem && 'w-[calc(100%-54px)] mr-auto sm:w-full'
                    )}
                    onClick={(e) => {
                      if (isFirstItem) {
                        e.stopPropagation();
                      }
                    }}
                  >
                    <SettlementProgress
                      value={settlement.isLocked ? 0 : settlement.purchasedCount}
                      max={settlementAmount ?? 0}
                      isBoosted={settlement.isBoosted}
                      className="w-full"
                    />
                    {isFirstItem && (
                      <Info className="min-w-[14px] w-[14px] text-card-foreground/80 cursor-pointer" />
                    )}
                  </div>
                </PopoverTrigger>

                <PopoverContent side="top" className="p-3 rounded-xl bg-background w-fit">
                  <Paragraph>{t('settlements.tooltip')}</Paragraph>
                </PopoverContent>
              </Popover>
            </div>
            {settlement.isPurchased && (
              <div className="flex gap-1 px-[2px]">
                <div
                  className={cn('flex items-center min-w-[63px]', isFirstItem && 'min-w-[34px]')}
                >
                  <span className="text-[12px] leading-6">💎</span>
                  <span className="text-base font-bold">
                    :{formatTonIncome(settlement.tonIncome)}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="text-[12px] leading-6">🐼</span>
                  <span className="text-base font-bold">:{settlement.pandas}</span>
                </div>
              </div>
            )}
          </div>

          <ul className="flex flex-col">
            {settlement.features.map((item) => (
              <li
                key={item}
                className={cn(
                  'flex gap-1 w-[118px] xs:w-[150px] sm:w-[155px]',
                  isFirstItem && 'sm:w-[118px]'
                )}
              >
                <span className="block w-[4px] h-[4px] bg-foreground rounded-full my-auto shrink-0"></span>
                <span className="text-ellipsis overflow-hidden whitespace-nowrap">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
};

const SettlementCard = ({
  settlement,
  align,
  shouldPulse,
  isFirstItem,
  isSecondItem,
  isThirdItem,
  isFourthItem,
  onClick,
  onGiftClick,
}: SettlementCardProps) => {
  const alignCfg = ALIGN_CLASSES[align];

  const { onClickHandler } = useSettlementClickAction({
    settlement,
    onSettlementClick: onClick,
  });

  const settlementElement = (
    <Settlement
      imageUrl={settlement.noBgImageUrl ?? ''}
      name={settlement.name}
      price={!settlement.isPurchased ? settlement.price : undefined}
      className={!settlement.isPurchased && !shouldPulse ? 'grayscale' : 'cursor-pointer'}
      imageClassName="size-[126px] -top-[10px] -right-[8px]"
      isLocked={settlement.isLocked}
    />
  );

  const handleGiftClick = (e: MouseEvent<HTMLImageElement>, nodeLevel: number) => {
    e.stopPropagation();
    telegramVibrate();
    onGiftClick(nodeLevel);
  };

  return (
    <div className={cn('flex flex-col gap-3', alignCfg.cardContainer)}>
      <div
        className={cn(
          'relative flex rounded-[11px] bg-card cursor-pointer',
          alignCfg.flexDirection
        )}
        onClick={onClickHandler}
      >
        {shouldPulse ? (
          <PulsingGlowWrapper borderWidth={4}>{settlementElement}</PulsingGlowWrapper>
        ) : (
          settlementElement
        )}

        <SettlementFeatures settlement={settlement} isFirstItem={isFirstItem} />

        {settlement.isBoosted && (
          <Button
            variant="custom"
            className={cn(
              'flex h-auto items-center justify-center rounded-none px-0.5',
              'bg-home-gradient-boost',
              alignCfg.boostArrowContainer
            )}
          >
            <ChevronsUp className="w-6 text-foreground" />
          </Button>
        )}

        {(isFirstItem || isSecondItem || isThirdItem || isFourthItem) && (
          <Image
            src={giftImage}
            className={cn(
              'w-[46px] absolute -bottom-[0.9rem] cursor-pointer',
              (isFirstItem || isThirdItem) && '-right-[2.9rem]',
              (isSecondItem || isFourthItem) && '-left-12'
              // settlement.isBoosted && '-right-[2.9rem]'
            )}
            onClick={(e) => {
              handleGiftClick(e, isFirstItem ? 0 : isSecondItem ? 1 : isThirdItem ? 2 : 3);
            }}
          />
        )}
      </div>

      <span
        className={cn(
          'w-fit rounded-full px-4 text-2xs font-semibold',
          'bg-home-chip text-home-blue-muted'
        )}
      >
        {settlement.name}
      </span>
    </div>
  );
};

const SettlementConnector = ({
  align,
  isNextSettlementPurchased,
  isPurchased,
}: SettlementConnectorProps) => {
  const alignCfg = ALIGN_CLASSES[align];

  let borderStyles;

  if (isPurchased) {
    if (!isNextSettlementPurchased) {
      borderStyles = 'animate-border-pulse';
    } else {
      borderStyles = 'border-[#04d9ff]';
    }
  } else {
    borderStyles = 'border-[#29B1E377]';
  }

  // p-[15.5px]
  return (
    <div className={cn('absolute -bottom-[48px] w-full ', alignCfg.connectorSide)}>
      <div
        className={cn(
          'h-[155px] w-full rounded-full border-10',
          alignCfg.connectorClipPath,
          borderStyles
        )}
      />
      <div className={cn('absolute top-1/2 size-9 -translate-y-1/2', alignCfg.connectorSide)} />
    </div>
  );
};

export function SettlementInfoCard({
  settlement,
  align,
  withConnector,
  isNextSettlementPurchased,
  id,
  shouldPulse,
  isFirstItem,
  isSecondItem,
  isThirdItem,
  isFourthItem,
  onClick,
  onGiftClick,
}: SettlementInfoCardProps) {
  const { t } = useTranslation();
  const tutorial = useAppSelector((state) => state.tutorial);

  const alignRight = align === 'right';
  const isTutorial = tutorial.step === 'HOME_SETTLEMENT_1' && isFirstItem;

  return (
    <div id={id} className="relative">
      {withConnector && (
        <SettlementConnector
          align={align}
          isNextSettlementPurchased={isNextSettlementPurchased}
          isPurchased={settlement.isPurchased}
        />
      )}
      <Tutorial text={t('tutorial.settlement.1_step')} enabled={isTutorial}>
        <div className={cn('relative w-[calc(100%-83px)]', alignRight && 'ml-auto')}>
          <SettlementCard
            settlement={settlement}
            align={align}
            shouldPulse={shouldPulse}
            isFirstItem={isFirstItem}
            isSecondItem={isSecondItem}
            isThirdItem={isThirdItem}
            isFourthItem={isFourthItem}
            onClick={onClick}
            onGiftClick={onGiftClick}
          />
        </div>
      </Tutorial>
    </div>
  );
}
